/**
 * CustomerImportPreviewDialog — preview seguro de uma importação de clientes.
 *
 * Reusa ImportPreviewShell e ImportPreviewMetricCard (padrão unificado de
 * imports). Dados servidos pela RPC `get_customer_import_preview` (limite 50,
 * com CPF/CNPJ/telefone/email mascarados no servidor).
 *
 * RELEASE: 2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */
import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  UserCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Inbox,
} from 'lucide-react';

import { useCustomerImportPreview } from '@/hooks/useCustomerImportPreview';
import { ImportPreviewShell } from '@/components/imports/shared/ImportPreviewShell';
import { ImportPreviewMetricCard } from '@/components/imports/shared/ImportPreviewMetricCard';
import { ImportPreviewErrorTooltip } from '@/components/imports/shared/ImportPreviewErrorTooltip';
import { Badge } from '@/components/ui/badge';

const STATUS_DOT: Record<string, string> = {
  pending: 'bg-muted-foreground',
  valid: 'bg-blue-500',
  invalid: 'bg-amber-500',
  duplicate: 'bg-amber-500',
  blacklisted: 'bg-red-500',
  processing: 'bg-blue-500',
  succeeded: 'bg-emerald-500',
  failed: 'bg-red-500',
  skipped: 'bg-muted-foreground',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  valid: 'Válido',
  invalid: 'Inválido',
  duplicate: 'Duplicado',
  blacklisted: 'Blacklist',
  processing: 'Processando',
  succeeded: 'Sucesso',
  failed: 'Falha',
  skipped: 'Ignorado',
};

export interface CustomerImportJobSummary {
  id: string;
  name: string;
  created_at?: string;
  total_rows?: number;
  succeeded_items?: number;
  failed_items?: number;
  invalid_count?: number;
  duplicate_count?: number;
  blacklisted_count?: number;
}

interface CustomerImportPreviewDialogProps {
  job: CustomerImportJobSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CustomerImportPreviewDialog({
  job,
  open,
  onOpenChange,
}: CustomerImportPreviewDialogProps) {
  const { data: items, isLoading } = useCustomerImportPreview(job.id, open);

  const invalidTotal = useMemo(
    () => (job.invalid_count ?? 0) + (job.duplicate_count ?? 0) + (job.blacklisted_count ?? 0),
    [job],
  );

  return (
    <ImportPreviewShell
      open={open}
      onOpenChange={onOpenChange}
      headerIcon={<UserCheck className="h-4 w-4" />}
      title={job.name}
      createdAt={job.created_at}
      metrics={
        <>
          <ImportPreviewMetricCard
            title="Total"
            value={job.total_rows ?? 0}
            icon={<Users className="h-4 w-4" />}
            variant="primary"
          />
          <ImportPreviewMetricCard
            title="Sucesso"
            value={job.succeeded_items ?? 0}
            icon={<CheckCircle2 className="h-4 w-4" />}
            variant="success"
          />
          <ImportPreviewMetricCard
            title="Inválidos"
            value={invalidTotal}
            icon={<AlertTriangle className="h-4 w-4" />}
            variant="info"
          />
          <ImportPreviewMetricCard
            title="Falhas"
            value={job.failed_items ?? 0}
            icon={<XCircle className="h-4 w-4" />}
            variant="danger"
          />
        </>
      }
    >
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Amostra de clientes importados</h3>

        {isLoading ? (
          <div className="rounded-md border overflow-hidden">
            <div className="space-y-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-none" />
              ))}
            </div>
          </div>
        ) : !items || items.length === 0 ? (
          <div className="rounded-md border border-dashed py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Inbox className="h-8 w-8 opacity-40" />
            <p className="text-sm">Nenhum item disponível para preview.</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <div className="max-h-[460px] overflow-auto">
              <Table className="min-w-[1100px]">
                <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/50">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 w-10 text-xs">#</TableHead>
                    <TableHead className="h-9 text-xs w-[70px]">Tipo</TableHead>
                    <TableHead className="h-9 text-xs w-[180px]">Nome / Razão Social</TableHead>
                    <TableHead className="h-9 text-xs w-[140px]">CPF / CNPJ</TableHead>
                    <TableHead className="h-9 text-xs w-[150px]">Telefone</TableHead>
                    <TableHead className="h-9 text-xs w-[180px]">E-mail</TableHead>
                    <TableHead className="h-9 text-xs w-[120px]">Cidade</TableHead>
                    <TableHead className="h-9 text-xs w-[70px]">UF</TableHead>
                    <TableHead className="h-9 text-xs w-[110px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const isPF = (it.customer_type ?? '').toLowerCase() === 'pf';
                    const displayName = isPF ? it.nome : it.razao_social ?? it.nome;
                    const idDoc = isPF ? it.cpf_masked : it.cnpj_masked;
                    return (
                      <TableRow key={it.id} className="text-xs">
                        <TableCell className="py-2 text-muted-foreground tabular-nums">
                          {it.row_index}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal uppercase">
                            {it.customer_type ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 font-medium max-w-[180px] truncate" title={displayName ?? ''}>
                          {displayName || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 font-mono text-[11px] whitespace-nowrap">
                          {idDoc || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 font-mono tabular-nums whitespace-nowrap">
                          {it.phone_masked || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[180px] truncate">
                          {it.email_masked || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[120px] truncate">
                          {it.cidade || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground uppercase">
                          {it.estado || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2">
                          <ImportPreviewErrorTooltip
                            colorClass={STATUS_DOT[it.status] ?? 'bg-muted-foreground'}
                            label={STATUS_LABEL[it.status] ?? it.status}
                            errorMessage={it.error_message}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </ImportPreviewShell>
  );
}
